'use client'
import { financeKeys } from '@/lib/analytics-finance/query-keys'
import { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { User, AuthChangeEvent, Session } from '@supabase/supabase-js'
import { UserProfile } from '@/types/user'
import { getSupabaseBrowserClient } from '@/lib/supabase-client'
import { toast } from 'sonner'
import { useRouter } from 'next/navigation'
import { GlobalLoading } from '@/components/ui/global-loading'
import { queryClient } from '@/lib/react-query'
import { aiKeys } from '@/queries/queryKeys'

type AuthContextType = {
  user: User | null
  profile: UserProfile | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  updateProfile: (data: { name?: string; viewedTours?: string[] }) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  resetPassword: (email: string) => Promise<void>
  updatePassword: (newPassword: string) => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  signIn: async () => {},
  signOut: async () => {},
  updateProfile: async () => {},
  changePassword: async () => {},
  resetPassword: async () => {},
  updatePassword: async () => {},
  refreshProfile: async () => {},
})

export { AuthContext }

/**
 * Cache de IA (Novo Sinistro + "Decisões da IA" do detalhe, ARCHITECTURE.md §10) some
 * junto com a sessão: sem isso, a próxima pessoa a logar no mesmo navegador podia
 * reaproveitar do cache dados (inclusive `run` — modelo/tokens/custo, só de ADMIN) de
 * outra sessão. `removeQueries` (não `invalidateQueries`): a sessão já foi, não há
 * quem refetch.
 */
function clearAiQueryCache(): void {
  queryClient.removeQueries({ queryKey: financeKeys.all })
  queryClient.removeQueries({ queryKey: aiKeys.all })
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const router = useRouter()
  const previousUserIdRef = useRef<string | null>(null)
  const isProcessingAuthChange = useRef(false) // 🚀 TOKEN REFRESH FIX: Evitar processamento simultâneo
  
  // 🚀 PERFORMANCE FIX: Memoizar supabaseClient para evitar recriação
  const supabaseClient = useMemo(() => getSupabaseBrowserClient(), [])

  // 🚀 PERFORMANCE FIX: Memoizar funções para evitar re-renders em componentes filhos
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { error } = await supabaseClient.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw error

      const profileResponse = await fetch('/api/auth/profile', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
        },
      })

      if (!profileResponse.ok) {
        await supabaseClient.auth.signOut()
        throw new Error('LOGIN_REFUSED')
      }
      
      toast.success('Login realizado com sucesso!', {
        style: {
          background: '#10B981',
          color: '#fff',
        },
      })
    } catch (error) {
      toast.error('Erro ao fazer login. Verifique suas credenciais.', {
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      })
      throw error
    }
  }, [supabaseClient])

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabaseClient.auth.signOut()
      if (error) throw error
      // Harness de IA (ARCHITECTURE.md §10): métricas de execução (modelo/tokens/custo)
      // só vão para ADMIN, mas a query key não carrega quem pediu — sem isso, a próxima
      // pessoa a logar no MESMO navegador podia herdar do cache um "run" que não é dela.
      clearAiQueryCache()

      toast.success('Logout realizado com sucesso!', {
        style: {
          background: '#10B981',
          color: '#fff',
        },
      })
    } catch (error) {
      toast.error('Erro ao fazer logout.', {
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      })
      throw error
    }
  }, [supabaseClient])

  // 🚀 SENIOR PATTERN: Centralizar lógica de atualização de perfil no AuthContext
  const updateProfile = useCallback(async (data: { name?: string; viewedTours?: string[] }) => {
    try {
      const response = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate', // 🚀 CACHE FIX: Force fresh data
        },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Erro ao atualizar perfil')
      }

      const updatedProfile = await response.json()
      setProfile(updatedProfile) // 🚀 Atualizar estado local

      // Só mostrar toast se não for apenas tours (silencioso para tours)
      if (data.name) {
        toast.success('Perfil atualizado com sucesso!', {
          style: {
            background: '#10B981',
            color: '#fff',
          },
        })
      }

    } catch (error) {
      console.error('Erro ao atualizar perfil:', error)
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar perfil', {
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      })
      throw error
    }
  }, [profile])

  // 🚀 SENIOR PATTERN: Centralizar lógica de alteração de senha no AuthContext
  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    try {
      if (!currentPassword) {
        throw new Error('Senha atual é obrigatória')
      }

      if (!newPassword) {
        throw new Error('Nova senha é obrigatória')
      }

      if (newPassword.length < 6) {
        throw new Error('Nova senha deve ter pelo menos 6 caracteres')
      }

      if (currentPassword === newPassword) {
        throw new Error('A nova senha deve ser diferente da senha atual')
      }

      // Verificar senha atual via Supabase Auth
      const { error: signInError } = await supabaseClient.auth.signInWithPassword({
        email: profile?.email || '',
        password: currentPassword,
      })

      if (signInError) {
        throw new Error('Senha atual incorreta')
      }

      // Alterar senha usando Supabase Auth
      const { error: updateError } = await supabaseClient.auth.updateUser({
        password: newPassword,
      })

      if (updateError) {
        throw updateError
      }

      toast.success('Senha alterada com sucesso!', {
        style: {
          background: '#10B981',
          color: '#fff',
        },
      })

    } catch (error) {
      console.error('Erro ao alterar senha:', error)
      toast.error(error instanceof Error ? error.message : 'Erro ao alterar senha', {
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      })
      throw error
    }
  }, [supabaseClient, profile?.email])

  // 🚀 SENIOR PATTERN: Função para reset de senha via e-mail
  const resetPassword = useCallback(async (email: string) => {
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Erro ao enviar e-mail de redefinição')
      }

      toast.success('E-mail de redefinição enviado com sucesso!', {
        style: {
          background: '#10B981',
          color: '#fff',
        },
      })

    } catch (error) {
      console.error('Erro ao solicitar reset de senha:', error)
      toast.error(error instanceof Error ? error.message : 'Erro ao enviar e-mail de redefinição', {
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      })
      throw error
    }
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Erro ao atualizar senha')
      }

      toast.success('Senha atualizada com sucesso!', {
        style: {
          background: '#10B981',
          color: '#fff',
        },
      })

    } catch (error) {
      console.error('Erro ao atualizar senha:', error)
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar senha', {
        style: {
          background: '#EF4444',
          color: '#fff',
        },
      })
      throw error
    }
  }, [])

  // 🚀 SENIOR PATTERN: Função para atualizar profile quando necessário (ex: após upload de avatar)
  const refreshProfile = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/profile', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache',
        }
      })
      
      if (response.ok) {
        const updatedProfile = await response.json()
        

        
        setProfile(updatedProfile)
      }
    } catch (error) {
      console.error('Erro ao atualizar profile:', error)
    }
  }, [])

  // 🚀 PERFORMANCE FIX: Remover dependência [profile] que causava loops infinitos
  useEffect(() => {
    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
      // 🚀 TOKEN REFRESH FIX: Evitar processamento simultâneo que causa loops
      if (isProcessingAuthChange.current) {
        console.log('🔄 Auth change já em processamento, ignorando...')
        return
      }
      
      isProcessingAuthChange.current = true
      console.log('🔐 Auth state change:', event, 'UserId:', session?.user?.id)
      
      const currentUserId = session?.user?.id || null
      const previousUserId = previousUserIdRef.current
      
      setUser(session?.user ?? null)

      if (session?.user) {
        // 🚀 PERFORMANCE FIX: Verificar se já temos profile do mesmo usuário
        if (currentUserId === previousUserId && previousUserIdRef.current) {
          setLoading(false)
          isProcessingAuthChange.current = false // 🚀 TOKEN REFRESH FIX: Liberar flag
          return
        }

        // Só limpa o profile se for um usuário diferente
        if (currentUserId !== previousUserId) {
          setProfile(null)
          setLoading(true) // Mostrar loading apenas quando necessário
        }

        try {
          // 🚀 TOKEN REFRESH FIX: Aguardar um tick para evitar chamadas simultâneas
          await new Promise(resolve => setTimeout(resolve, 100))
          
          const response = await fetch('/api/auth/profile', {
            method: 'GET',
            credentials: 'include', // 🚀 CRITICAL FIX: Garantir que cookies sejam enviados
            headers: {
              'Cache-Control': 'no-cache', // 🚀 TOKEN REFRESH FIX: Evitar cache que pode causar loops
            }
          })
          
          if (response.ok) {
            const userProfile = await response.json()
            

            
            setProfile(userProfile)
            previousUserIdRef.current = currentUserId
          } else {
            // Se a resposta não for ok, desloga o usuário
            console.error('Erro ao buscar profile do usuário:', response.status, response.statusText)
            await supabaseClient.auth.signOut()
            toast.error('Erro ao carregar dados do usuário. Entre em contato com o suporte.', {
              style: {
                background: '#EF4444',
                color: '#fff',
              },
              duration: 10000,
            })
            // 🚀 TOKEN REFRESH FIX: Aguardar antes de redirecionar para evitar loops
            if (router && typeof window !== 'undefined' && window.location.pathname !== '/login') {
              setTimeout(() => router.push('/login'), 200)
            }
            previousUserIdRef.current = null
            isProcessingAuthChange.current = false // 🚀 TOKEN REFRESH FIX: Liberar flag no primeiro erro
          }
        } catch (error) {
          console.error('Erro na requisição do profile:', error)
          await supabaseClient.auth.signOut()
          toast.error('Erro de conexão ao carregar dados do usuário. Entre em contato com o suporte.', {
            style: {
              background: '#EF4444',
              color: '#fff',
            },
            duration: 10000,
          })
          // 🚀 TOKEN REFRESH FIX: Aguardar antes de redirecionar para evitar loops
          if (router && typeof window !== 'undefined' && window.location.pathname !== '/login') {
            setTimeout(() => router.push('/login'), 200)
          }
          previousUserIdRef.current = null
          isProcessingAuthChange.current = false // 🚀 TOKEN REFRESH FIX: Liberar flag no segundo erro
        } finally {
          setLoading(false)
          isProcessingAuthChange.current = false // 🚀 TOKEN REFRESH FIX: Liberar flag
        }
      } else {
        // Se não houver sessão, limpa tudo (login expirado, sessão inválida no servidor,
        // ou signOut explícito — todos chegam aqui, então é o único lugar que garante a
        // limpeza do cache de IA independente de qual caminho encerrou a sessão).
        setProfile(null)
        previousUserIdRef.current = null
        clearAiQueryCache()
        // 🚀 TOKEN REFRESH FIX: Aguardar antes de redirecionar para evitar loops
        if (router && typeof window !== 'undefined' && window.location.pathname !== '/login') {
          setTimeout(() => router.push('/login'), 200)
        }
        setLoading(false)
        isProcessingAuthChange.current = false // 🚀 TOKEN REFRESH FIX: Liberar flag
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [supabaseClient, router]) // 🚀 CRITICAL FIX: Removida dependência [profile]

  // 🚀 PERFORMANCE FIX: Memoizar context value para evitar re-renders
  const contextValue = useMemo(() => ({
    user,
    profile,
    loading,
    signIn,
    signOut,
    updateProfile,
    changePassword,
    resetPassword,
    updatePassword,
    refreshProfile
  }), [user, profile, loading, signIn, signOut, updateProfile, changePassword, resetPassword, updatePassword, refreshProfile])

  return (
    <AuthContext.Provider value={contextValue}>
      {loading ? (
        <GlobalLoading />
      ) : (
        children
      )}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider')
  }
  return context
} 